import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Image,
  Box,
  Loader,
  Flex,
  Center,
  CopyButton,
  ActionIcon,
  Alert,
  rem,
} from "@mantine/core";
import {
  IconBolt,
  IconCheck,
  IconHeart,
  IconHeartFilled,
} from "@tabler/icons-react";
import ClearIcon from "../../../assets/clear_logo_midnight.svg";
import ClearIconWhite from "../../../assets/clear_logo_white.svg";
import ClearFullLogo from "../../../assets/clear_full_logo.svg";
import { DataContext } from "../../../context/DataContext";
import { isMobile } from "react-device-detect";
import QRCode from "qrcode.react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../../utils/edge";

const ClearStart = () => {
  const { settings, data } = useContext(DataContext);
  const [url, setUrl] = useState(null);
  const [requestId, setRequestId] = useState(null);

  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  const handleVerifyClick = () => {
    window.open(url, "_blank");
  };

  // Polling function for edge mode
  const pollForUrl = async (reqId) => {
    const maxAttempts = 100;
    const pollInterval = 3000;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: reqId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.request_url) {
            setUrl(result.request_url);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No URL received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (url) return;
    const fetchUrl = async () => {
      data.provider = "CLEAR";
      try {
        let apiUrl;
        let requestData = { ...data };

        if (isEdgeMode) {
          apiUrl = getEdgeQueueUrl();
          requestData.queue = "idmax";
        } else {
          apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
        }

        const response = await fetch(apiUrl, {
          method: "POST",
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data_returned = await response.json();

        if (isEdgeMode) {
          const reqId = data_returned.request_id || data.request_id;
          if (reqId) {
            setRequestId(reqId);
            console.log("Starting polling for request_id:", reqId);
            pollForUrl(reqId);
          } else {
            console.error("No request_id found in edge response");
          }
        } else {
          setUrl(data_returned.continuations.info.template.info.request_url);
        }
      } catch (error) {
        console.error("Error fetching status:", error);
      }
    };

    fetchUrl();
  }, [url]);

  return (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          <Stack>
            <Center>
              <Image src={ClearFullLogo} width={150} />
            </Center>

            <Center mt="lg">
              <Stack>
                <Group noWrap>
                  <IconBolt size={20} />
                  <Text fz="md">Fast-track with existing CLEAR account.</Text>
                </Group>
                <Group noWrap>
                  <Image src={ClearIcon} width={20} />
                  <Text fz="md">
                    Verify without an account — completely free.
                  </Text>
                </Group>
                <Group noWrap>
                  <IconHeart size={20} />
                  <Text fz="md">Trusted by millions.</Text>
                </Group>
              </Stack>
            </Center>
          </Stack>
          {!isMobile && (
            <Stack align="center" mt="md">
              <Box
                style={{
                  height: 200,
                  width: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {url ? <QRCode value={url} size={200} /> : <Loader />}
              </Box>
            </Stack>
          )}
        </Box>

        <Box>
          <Box mb="md">
            <Alert
              icon={<IconCheck />}
              title={"Important!"}
              styles={(theme) => ({
                root: {
                  padding: rem(3),
                },
              })}
            >
              No account is required — it's secure, easy, and free for you.
            </Alert>
          </Box>
          {!isMobile ? (
            <Group position="center" mt="md">
              <Loader size="sm" />
              <Text ta="center" c="dimmed" fz="sm">
                Checking your status...
              </Text>
            </Group>
          ) : !url ? (
            <Center mt="lg">
              <Group>
                <Loader />
                <Text>Getting ready...</Text>
              </Group>
            </Center>
          ) : (
            <Button
              size="lg"
              mt="lg"
              leftIcon={<Image src={ClearIconWhite} width={24} />}
              style={{
                backgroundColor: "#041A55",
                color: "#ffffff",
              }}
              onClick={handleVerifyClick}
              fullWidth
            >
              Verify with CLEAR
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ClearStart;
